import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Box, Button, Container, Divider, Paper, TextField, Typography, Alert, Link as MuiLink } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { clientLogin, clientGoogleLogin, clientCheckAuth } from "../../../store/thunks/clientAuthThunks";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

const schema = z.object({
  rfc: z.string().min(12, "RFC debe tener 12 o 13 caracteres").max(13),
  password: z.string().min(6, "Mínimo 6 caracteres"),
});

type FormData = z.infer<typeof schema>;

export const LoginPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { error, isLoading, isAuthenticated } = useAppSelector((state) => state.clientAuth);

  useEffect(() => {
    dispatch(clientCheckAuth());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/facturacion", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const { control, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { rfc: "", password: "" },
  });

  const onSubmit = (data: FormData) => {
    dispatch(clientLogin(data.rfc, data.password));
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" align="center" gutterBottom fontWeight="bold" sx={{ color: "#0e368d" }}>
            Portal de Facturación
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
            Ingresa con tu RFC para solicitar y consultar tus facturas
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="rfc"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="RFC"
                  fullWidth
                  margin="normal"
                  error={!!errors.rfc}
                  helperText={errors.rfc?.message}
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  autoFocus
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Contraseña"
                  type="password"
                  fullWidth
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  autoComplete="current-password"
                />
              )}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading}
              sx={{ mt: 2, mb: 2, backgroundColor: "#0e368d", "&:hover": { backgroundColor: "#0b2b71" } }}
            >
              {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
            </Button>
          </form>

          <Typography variant="body2" align="center">
            <MuiLink component={Link} to="/facturacion/recuperar-contrasena">
              ¿Olvidaste tu contraseña o aún no tienes una?
            </MuiLink>
          </Typography>

          <Divider sx={{ my: 2 }}>o</Divider>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                if (credentialResponse.credential) {
                  dispatch(clientGoogleLogin(credentialResponse.credential));
                }
              }}
              onError={() => {
                console.error("Google login failed");
              }}
              text="signin_with"
            />
          </Box>

          <Typography variant="body2" align="center">
            ¿No tienes cuenta?{" "}
            <MuiLink component={Link} to="/facturacion/registro">
              Regístrate aquí
            </MuiLink>
          </Typography>
        </Paper>
      </Container>
    </GoogleOAuthProvider>
  );
};
